import { styled } from "@pigment-css/react";
import { Bar } from "react-chartjs-2";
import type { ChartOptions, ActiveElement, ChartEvent, ScriptableContext } from "chart.js";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { useLocale } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { formatCurrency } from "@acto/taskpane/core/utils/formatters";
import { formatCompact } from "@acto/taskpane/field-sales-2/utils/format";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ChartWrapper = styled("div")(() => ({
  height: 300,
}));

const LABEL_DISPLAY_THRESHOLD = 8;

export interface BarChartProps {
  labels: string[];
  values: number[];
  selectedMonth?: number | null;
  onMonthSelect?: (index: number | null) => void;
  height?: number;
  barColor?: string;
  selectedBarColor?: string;
  /** Tooltip value; defaults to the tenant currency. */
  formatValue?: (value: number) => string;
  integerYTicks?: boolean;
}

export const BarChart = ({
  labels,
  values,
  selectedMonth,
  onMonthSelect,
  height = 300,
  barColor = "#334EFF",
  selectedBarColor = "#1a2db5",
  formatValue,
  integerYTicks = false,
}: BarChartProps) => {
  const { locale } = useLocale();
  const currency = useCurrencyContext();

  const chartOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (_: ChartEvent, elements: ActiveElement[]) => {
      if (onMonthSelect) {
        if (elements.length > 0) {
          const clickedIndex = elements[0].index;
          onMonthSelect(clickedIndex === selectedMonth ? null : clickedIndex);
        } else {
          onMonthSelect(null);
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            if (formatValue) {
              return formatValue(value ?? 0);
            }
            if (value == null || value === undefined) {
              return `0${currency}`;
            }
            return formatCurrency(value, locale, currency);
          },
        },
      },
    },
    scales: {
      x: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: "Alliance No.2",
            size: 12,
          },
          // Show every 2nd label on small screens to avoid rotation
          callback: function (value, index) {
            const totalLabels = this.chart.data.labels?.length || 0;
            // If more than LABEL_DISPLAY_THRESHOLD labels, show every 2nd one
            if (totalLabels > LABEL_DISPLAY_THRESHOLD && index % 2 !== 0) {
              return "";
            }
            return this.getLabelForValue(value as number);
          },
        },
      },
      y: {
        border: {
          display: false,
        },
        ticks: {
          padding: 12,
          precision: integerYTicks ? 0 : undefined,
          font: {
            family: "Alliance No.2",
            size: 12,
          },
          callback: (value) => {
            return formatCompact(Number(value));
          },
        },
      },
    },
  };

  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        borderRadius: 8,
        borderSkipped: false,
        barThickness: 8,
        backgroundColor: (context: ScriptableContext<"bar">) => {
          const index = context.dataIndex;
          if (index === selectedMonth) {
            return selectedBarColor;
          }
          return barColor;
        },
      },
    ],
  };

  return (
    <ChartWrapper style={{ height }}>
      <Bar data={chartData} options={chartOptions} />
    </ChartWrapper>
  );
};
